import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { PostContact } from '../../store/contactSlice';
import { DEFAULT_SERVICE } from './serviceOptions';
import { getTurnstileToken } from './turnstile';
import { trackFormEvent } from '../../lib/analytics';
import * as store from '../../lib/localStore';

// crypto.randomUUID() is available in every browser CRA's browserslist targets
// (Chrome 92+, FF 95+, Safari 15.4+). Fallback only for ancient/test environments.
const generateIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `tn-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const splitName = (full) => {
  if (!full) return { firstName: null, lastName: null };
  const parts = full.trim().split(/\s+/);
  return {
    firstName: parts[0] || null,
    lastName: parts.length > 1 ? parts.slice(1).join(' ') : null,
  };
};

const buildPayload = (values, { serviceTag } = {}) => {
  const fromFirst = values.firstName?.trim();
  const fromLast = values.lastName?.trim();
  const fromFull = values.name?.trim();

  let firstName = null;
  let lastName = null;
  if (fromFirst || fromLast) {
    firstName = fromFirst || null;
    lastName = fromLast || null;
  } else if (fromFull) {
    const split = splitName(fromFull);
    firstName = split.firstName;
    lastName = split.lastName;
  }

  return {
    firstName,
    lastName,
    email: values.email?.trim() || null,
    phone: values.phone?.trim() || null,
    companyName: values.company?.trim() || values.companyName?.trim() || null,
    service: values.service || serviceTag || DEFAULT_SERVICE,
    projectBudget: values.projectBudget?.trim() || null,
    projectDescription: values.projectDescription?.trim() || null,
    totalEmployees: values.totalEmployees?.trim() || null,
    location: values.location?.trim() || null,
    message: values.message?.trim() || null,
  };
};

const defaultBuildPayload = buildPayload;

// Drafts auto-expire after 14 days so we don't keep stale half-typed
// project descriptions around forever. The honeypot field is never persisted.
const DRAFT_TTL_MS = 14 * 24 * 60 * 60 * 1000;
const SENSITIVE_DRAFT_FIELDS = ['website']; // honeypot

const stripSensitive = (values) => {
  const clone = { ...values };
  SENSITIVE_DRAFT_FIELDS.forEach((k) => delete clone[k]);
  // File objects can't be JSON-serialised; skip them.
  Object.keys(clone).forEach((k) => {
    if (clone[k] instanceof File || clone[k] instanceof Blob) delete clone[k];
  });
  return clone;
};

const hasMeaningfulContent = (values) =>
  Object.values(values).some((v) =>
    typeof v === 'string' ? v.trim().length > 1 : Boolean(v)
  );

export const useContactForm = ({
  initialValues = {},
  validators = {},
  serviceTag,
  composePayload,
  successMessage,
  onSuccess,
  // formName: used as analytics dimension + draft storage key. Optional —
  // if omitted, autosave is disabled (we don't know how to key it).
  formName,
} = {}) => {
  const dispatch = useDispatch();
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [draftRestored, setDraftRestored] = useState(false);
  const draftKey = formName ? `form-draft:${formName}` : null;

  // Restore draft on mount when a formName is provided. We hydrate values
  // once; subsequent props.initialValues changes don't re-trigger restore.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!draftKey) return;
    const draft = store.get(draftKey);
    if (draft && typeof draft === 'object') {
      setValues((prev) => ({ ...prev, ...draft }));
      setDraftRestored(true);
    }
  }, [draftKey]);

  // Persist every state change (debounced via rAF batching by React itself,
  // localStorage.setItem ~0.1ms). Skip until user has typed something
  // meaningful so we don't write an empty draft on mount.
  useEffect(() => {
    if (!draftKey) return;
    if (isSubmitting) return;
    if (!hasMeaningfulContent(values)) return;
    store.set(draftKey, stripSensitive(values), { ttlMs: DRAFT_TTL_MS });
  }, [draftKey, values, isSubmitting]);

  const runValidators = useCallback((nextValues) => {
    const nextErrors = {};
    for (const field of Object.keys(validators)) {
      const rules = Array.isArray(validators[field]) ? validators[field] : [validators[field]];
      for (const rule of rules) {
        if (typeof rule !== 'function') continue;
        const error = rule(nextValues[field], nextValues);
        if (error) {
          nextErrors[field] = error;
          break;
        }
      }
    }
    return nextErrors;
  }, [validators]);

  const handleChange = useCallback((event) => {
    const { name, value, type, checked, files } = event.target;
    const nextValue = type === 'checkbox' ? checked : type === 'file' ? files?.[0] : value;
    setValues((prev) => ({ ...prev, [name]: nextValue }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
    if (status.message) setStatus({ type: '', message: '' });
    if (draftRestored) setDraftRestored(false);
  }, [status.message, draftRestored]);

  const setFieldValue = useCallback((name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  }, []);

  const initialValuesRef = useRef(initialValues);
  const reset = useCallback(() => {
    setValues(initialValuesRef.current);
    setErrors({});
    setStatus({ type: '', message: '' });
    if (draftKey) store.remove(draftKey);
  }, [draftKey]);

  const clearDraft = useCallback(() => {
    if (draftKey) store.remove(draftKey);
    setDraftRestored(false);
  }, [draftKey]);

  const handleSubmit = useCallback(async (event) => {
    if (event?.preventDefault) event.preventDefault();

    // Honeypot: an invisible input named `website` is rendered by SubmitButton.
    // Real users never type into it; bots crawl the DOM and fill every field.
    // We pretend success so the bot doesn't retry, but skip the network call.
    const honeypot = event?.target?.elements?.website?.value;
    if (honeypot) {
      setStatus({
        type: 'success',
        message: successMessage || "Thanks! We'll be in touch within one business day.",
      });
      setValues(initialValuesRef.current);
      if (draftKey) store.remove(draftKey);
      return;
    }

    // Guard against double-submit even if the consumer forgot to disable the button.
    if (isSubmitting) return;

    const validationErrors = runValidators(values);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setStatus({ type: 'error', message: 'Please fix the highlighted fields and try again.' });
      trackFormEvent('validation_error', {
        formName: formName || 'contact',
        invalidFields: Object.keys(validationErrors).slice(0, 5).join(','),
      });
      return;
    }
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });
    trackFormEvent('attempt', { formName: formName || 'contact' });
    try {
      const base = defaultBuildPayload(values, { serviceTag });
      const payload = typeof composePayload === 'function'
        ? composePayload(values, base) || base
        : base;

      // Turnstile: no-op (resolves null) when REACT_APP_TURNSTILE_SITEKEY isn't set,
      // so the form behaves identically to today until the sitekey is configured.
      const turnstileToken = await getTurnstileToken();

      await dispatch(PostContact({
        payload,
        idempotencyKey: generateIdempotencyKey(),
        turnstileToken,
      })).unwrap();

      setStatus({
        type: 'success',
        message: successMessage || "Thanks! We'll be in touch within one business day.",
      });
      setValues(initialValuesRef.current);
      if (draftKey) store.remove(draftKey);
      trackFormEvent('success', { formName: formName || 'contact' });
      if (onSuccess) onSuccess(payload);
    } catch (error) {
      // Slice now surfaces the backend message via rejectWithValue, so prefer it
      // verbatim when available rather than swallowing it into a generic copy.
      const message = typeof error === 'string'
        ? error
        : error?.message || 'Something went wrong. Please try again or email connect@technumopus.com.';
      setStatus({ type: 'error', message });
      trackFormEvent('failure', { formName: formName || 'contact', reason: String(message).slice(0, 80) });
    } finally {
      setIsSubmitting(false);
    }
  }, [composePayload, dispatch, draftKey, formName, isSubmitting, onSuccess, runValidators, serviceTag, successMessage, values]);

  return {
    values,
    errors,
    status,
    isSubmitting,
    handleChange,
    handleSubmit,
    setFieldValue,
    reset,
    // New API surface — consumers that want to show a "draft restored" toast
    // or a "clear draft" link can opt in. Existing call sites unaffected.
    draftRestored,
    clearDraft,
  };
};

export default useContactForm;
